// =============================================================================
// domain/dashBreakdowns.ts — the Dashboard's three breakdown cards.
// -----------------------------------------------------------------------------
// P&L by Weekday, P&L by Session, Top Patterns. All three are horizontal bar
// lists over the SAME population, and all three are Class W — they describe the
// active window, so filtering a superset gives the same answer.
//
// ── 🚨 THE POPULATION IS THE DASHBOARD'S, WHICH IS A FOURTH ONE (D77) ──
//
// `tradesForActive()` — the active date range AND the account selection — then
// `countsForMetrics`. That is not any of the three already ported:
//
//   Dashboard        active range + selection + countsForMetrics
//   Setup Quality    ALL accounts, unbounded + countsForMetrics
//   Pattern Playbook whole book, `annotated !== false`, no account scope
//   Exit Models      whole book, `annotated !== false`, no account scope
//
// ⚠ AND TOP PATTERNS IS A FIFTH. Beta computes it as
// `patternStats(p.id, trades, unit)` — passing the dashboard's trades INTO a
// function that then applies its own `annotated !== false` on top. So Top
// Patterns is the dashboard population INTERSECTED with the reviewed one, and it
// is the only card here that drops unreviewed trades.
//
// That is genuinely what beta does and it is genuinely inconsistent with the two
// cards beside it. Matched, and asserted by a test that names the intersection,
// because a reader comparing the three cards will otherwise "fix" it.
// =============================================================================

import type { LeaderboardTrade } from './patternLeaderboard';
import { computeLeaderboardStats } from './patternLeaderboard';

export interface BreakdownRow {
  key: string;
  value: number;
}

/** What the breakdowns need from a trade. */
export interface BreakdownTrade extends LeaderboardTrade {
  /** The day-boundary-resolved date — the caller applies the mode. */
  effectiveDate?: string | null;
  /** `'NY-AM'`, `'EU'`, … Empty when the trade was never session-tagged. */
  sessionLabel?: string | null;
  annotated?: boolean | null;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** The old client's `SESSIONS`, in its order. */
export const SESSIONS: readonly string[] = ['Asia', 'EU', 'NY-AM', 'NY-PM'];

/** `2026-07-31` → 5 (Friday). UTC, so the label never shifts by timezone. */
function dayOfWeek(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getUTCDay();
}

/**
 * P&L by weekday.
 *
 * ⚠ Mon–Fri are ALWAYS shown, even at zero — a missing Wednesday reads as a
 * rendering fault, and "I did not trade Wednesday" is itself a finding. Saturday
 * and Sunday appear only when they carry a non-zero total, because a futures
 * trader has no weekend session and two permanent empty rows are noise.
 *
 * ⚠ `if (sums[wd])` in beta, which is a TRUTHINESS test: a weekend day whose
 * trades sum to exactly $0 is omitted even though it had trades. Matched — the
 * alternative is a Saturday row appearing at $0 on the one screen where beta
 * shows five rows and everyone reads five rows.
 *
 * ⚠ The weekday comes from the EFFECTIVE date, so under session mode a trade
 * taken Monday 23:00 ET counts as Tuesday — its session day. Reading the raw
 * date here is the defect that put evening trades on the wrong day twice before.
 */
export function weekdayPnl(
  trades: readonly BreakdownTrade[],
  unitValue: (t: BreakdownTrade) => number | null,
): BreakdownRow[] {
  const sums = new Map<number, number>();
  for (const t of trades) {
    const d = t.effectiveDate ?? '';
    if (!d) continue;
    const wd = dayOfWeek(d);
    sums.set(wd, (sums.get(wd) ?? 0) + (unitValue(t) ?? 0));
  }
  const order = [1, 2, 3, 4, 5];
  for (const wd of [0, 6]) if (sums.get(wd)) order.push(wd);
  return order.map((wd) => ({ key: DAY_NAMES[wd] as string, value: sums.get(wd) ?? 0 }));
}

/**
 * P&L by session.
 *
 * ⚠ ONLY sessions that have tagged trades, in the catalogue's order — the
 * opposite default from weekdays, and beta's. A session is a manual tag rather
 * than a fact about the clock, so an empty `Asia` row would report a gap in the
 * tagging as a gap in the trading.
 *
 * ⚠ Untagged trades are DROPPED, not bucketed as "Unknown". They are still in
 * every other card on the screen, so the session totals do not add up to the
 * dashboard total — which is why the card is a breakdown by tag and is not
 * labelled as a decomposition of the whole.
 */
export function sessionPnl(
  trades: readonly BreakdownTrade[],
  unitValue: (t: BreakdownTrade) => number | null,
): BreakdownRow[] {
  const sums = new Map<string, number>();
  for (const t of trades) {
    const s = (t.sessionLabel ?? '').trim();
    if (!s) continue;
    sums.set(s, (sums.get(s) ?? 0) + (unitValue(t) ?? 0));
  }
  return SESSIONS.filter((s) => sums.has(s)).map((s) => ({ key: s, value: sums.get(s) ?? 0 }));
}

export interface TopPatternRow {
  id: string;
  name: string;
  expectancy: number;
  sample: number;
}

/**
 * The five patterns with the best expectancy in the active window.
 *
 * ⚠ ITS POPULATION IS THE INTERSECTION — see the header. Beta hands the
 * dashboard's trades to `patternStats`, which applies `annotated !== false` on
 * top, so this card silently drops unreviewed trades that the two cards beside
 * it count. The `reviewedOnly` flag exists to make that visible at the call site
 * rather than buried, and it defaults to the beta behaviour.
 *
 * ⚠ RANKED BY EXPECTANCY, so a one-trade pattern can head the list. That is
 * beta's, and the sample is rendered beside each row precisely because of it.
 *
 * ⚠ `sample > 0` — a pattern in the catalogue that has no trades in the window
 * is absent rather than listed at zero. The opposite call from the Playbook,
 * where an untagged pattern must appear because that screen IS the catalogue.
 */
export function topPatterns(
  trades: readonly BreakdownTrade[],
  patterns: readonly { id: string; name: string }[],
  unitValue: (t: BreakdownTrade) => number | null,
  limit = 5,
  reviewedOnly = true,
): TopPatternRow[] {
  const scoped = reviewedOnly ? trades.filter((t) => t.annotated !== false) : trades;

  const byPattern = new Map<string, BreakdownTrade[]>();
  for (const t of scoped) {
    const id = t.patternId ? String(t.patternId) : '';
    if (!id) continue;
    const bucket = byPattern.get(id);
    if (bucket) bucket.push(t);
    else byPattern.set(id, [t]);
  }

  return patterns
    .map((p) => {
      const mine = byPattern.get(p.id) ?? [];
      // ⚠ Reused rather than recomputed, like every other board — expectancy
      // skips trades with no value in the unit rather than counting them zero,
      // and a second implementation would drift from the Playbook's number on a
      // screen the user reads minutes apart.
      const s = computeLeaderboardStats(mine, unitValue);
      return { id: p.id, name: p.name || 'Untitled', expectancy: s.expectancy, sample: s.sample };
    })
    .filter((r) => r.sample > 0)
    .sort((a, b) => b.expectancy - a.expectancy || a.name.localeCompare(b.name))
    .slice(0, limit);
}
